import { BNAccordionMenu } from '../../../components/Accordion/BNAccordion';
import { ActionIcon, Box, Center, Checkbox, Flex, Group, Text, Tooltip } from '@mantine/core';
import classes from './SharingMenu.module.css';
import { ReactNode, useEffect } from 'react';
import cx from 'clsx';
import MarketplaceStubHubIcon from '../../../components/icons/MarketplaceStubHub';
import MarketplaceVividIcon from '../../../components/icons/MarketplaceVivid';
import MarketplaceTicketmasterIcon from '../../../components/icons/MarketplaceTicketmaster';
import MarketplaceSeatGeekIcon from '../../../components/icons/MarketplaceSeatGeek';
import MarketplaceTicketEvolutionIcon from '../../../components/icons/MarketplaceTicketEvolution';
import MarketplaceTickPickIcon from '../../../components/icons/MarketplaceTickPick';
import MarketplaceAXSIcon from '../../../components/icons/MarketplaceAXS';
import MarketplaceGametimeIcon from '../../../components/icons/MarketplaceGametime';
import MarketplaceTicketNetworkIcon from '../../../components/icons/MarketplaceTicketNetwork';
import MarketplaceGoTicketsIcon from '../../../components/icons/MarketplaceGoTickets';
import { BNButton } from '../../../components/Button/Button';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectedMergedListingAtom, updateListingsAtom } from '../../../data/atoms';
import ShareIcon from '../../../components/icons/Share';
import { useBulkState } from '../../../data/Bulk.state';
import WarningIcon from '../../../components/icons/Warning';
import { generateNewInternalNotes, getSelectedMarketplaces, Marketplace, VLMarketplaces } from '../vl-utils';
import { useListingState } from '../../../data/Listing.state';
import { TenantIdListingId } from '../../../models/tenantIdListingId';
import { useForm } from '@mantine/form';

type SharingIconButtonProps = {
  icon: ReactNode;
  tooltipLabel?: ReactNode;
  selected?: boolean;
  onClick: () => void;
  disabled?: boolean;
};

function SharingIconButton({ icon, tooltipLabel, selected, onClick, disabled }: SharingIconButtonProps) {
  return (
    <Tooltip label={tooltipLabel} disabled={!tooltipLabel} withArrow>
      <ActionIcon miw={30} w={44} mih={30} h={30} variant="default" onClick={onClick} className={cx(selected && classes.btnSelected)} disabled={disabled}>
        {icon}
      </ActionIcon>
    </Tooltip>
  );
}

export function SharingMenuPanel({ onClose, onClick, bulk }: { onClose?: () => void; onClick?: () => void; bulk?: boolean }) {
  const updateListing = useSetAtom(updateListingsAtom);
  const { distinctTenantPointsOfSale } = useBulkState('distinctTenantPointsOfSale');
  const selectedListing = useAtomValue(selectedMergedListingAtom);
  const { tenantIdListingId } = selectedListing ?? {};
  const { tenantId, listingId } = TenantIdListingId.fromString(tenantIdListingId);

  const { patchListing, patchListingLoading } = useListingState('patchListing', 'patchListingLoading');

  const updateInternalNotes = async (note: string | null | undefined) => {
    if (selectedListing?.listingId && typeof note === 'string') {
      const { data: listingResult } = await patchListing({
        tenantId,
        listingId,
        data: [{ op: 'replace', path: '/internalNotes', value: note }],
      });
      if (listingResult) {
        updateListing(listingResult);
      }
      return listingResult;
    }
    return null;
  };

  const form = useForm<{ selectedMarketplaces: Marketplace[] }>({
    initialValues: {
      selectedMarketplaces: getSelectedMarketplaces(selectedListing?.internalNotes || ''),
    },
  });

  const { setValues, resetDirty } = form;

  useEffect(() => {
    const next = getSelectedMarketplaces(selectedListing?.internalNotes || '');
    setValues({ selectedMarketplaces: next });
    resetDirty(); // baseline dirty tracking
  }, [selectedListing?.internalNotes, selectedListing?.tenantIdListingId, resetDirty, setValues]);

  const activeMarketplaceSize = new Set(Object.values(VLMarketplaces).map((x) => x.value)).size;

  const checkedCount = form.values.selectedMarketplaces.length;
  const allSelected = checkedCount === activeMarketplaceSize;
  const partialSelected = checkedCount > 0 && checkedCount < activeMarketplaceSize;

  const toggleAllMarketplaces = () => {
    form.setFieldValue('selectedMarketplaces', allSelected ? [] : Object.values(VLMarketplaces));
  };

  const handleSelectionChange = (marketplace: Marketplace) => {
    const current = form.values.selectedMarketplaces;
    if (current.findIndex((m) => m.value === marketplace.value) !== -1) {
      form.setFieldValue(
        'selectedMarketplaces',
        current.filter((m) => m.value !== marketplace.value),
      );
    } else {
      form.setFieldValue('selectedMarketplaces', [...current, marketplace]);
    }
  };

  const showTooltip = false;

  if (!selectedListing) {
    return null;
  }

  return (
    <BNAccordionMenu.Panel
      id="sharing-options"
      leftSection={<ShareIcon />}
      disabled={distinctTenantPointsOfSale.length > 1 || !distinctTenantPointsOfSale.includes('VictoryLiveOne')}
      disabledText="Multiple points of sale selected"
      className={classes.sharingAccordionMenuItem}
      rightSection={
        !bulk && (
          <Flex className="marketplaceMenuCount" w={32} justify="end" pos="absolute" right={37} c="var(--colors-gray-5)" style={{ pointerEvents: 'none', whiteSpace: 'nowrap' }}>
            <Text fz={11} p={0} m={0}>
              <Text component="span" fz={11} p={0} m={0}>
                {checkedCount}
              </Text>
              &nbsp;/ {activeMarketplaceSize}
            </Text>
          </Flex>
        )
      }
      title="Marketplaces"
      onClick={onClick}
    >
      <Flex ml={23} direction="column" gap="xs">
        <Box pos="relative" maw={236}>
          <Box pos="absolute" top={-41} right={22} w={54}>
            <Checkbox
              checked={allSelected}
              disabled={selectedListing.quantityReserved > 0}
              onChange={toggleAllMarketplaces}
              indeterminate={partialSelected}
              label={
                <Flex w={32} justify="end" wrap="nowrap">
                  <Text component="span" fz={11} style={{ whiteSpace: 'nowrap' }} c="var(--colors-gray-5)">
                    <Text component="span" fz={11} fw={700} c="var(--colors-selectedBorder)">
                      {checkedCount}
                    </Text>
                    &nbsp;/ {activeMarketplaceSize}
                  </Text>
                </Flex>
              }
              size="xs"
              className={classes.sharingCheckbox}
            />
          </Box>
          <Flex gap={4} wrap="wrap" w={236} className={classes.sharingButtonGroup}>
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.StubHub}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.StubHub.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.StubHub)}
              icon={<MarketplaceStubHubIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces['Vivid Seats']}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces['Vivid Seats'].name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces['Vivid Seats'])}
              icon={<MarketplaceVividIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.Ticketmaster}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.Ticketmaster.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.Ticketmaster)}
              icon={<MarketplaceTicketmasterIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.SeatGeek}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.SeatGeek.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.SeatGeek)}
              icon={<MarketplaceSeatGeekIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces['Ticket Evolution']}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces['Ticket Evolution'].name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces['Ticket Evolution'])}
              icon={<MarketplaceTicketEvolutionIcon size={20} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.TickPick}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.TickPick.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.TickPick)}
              icon={<MarketplaceTickPickIcon size={20} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.AXS}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.AXS.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.AXS)}
              icon={<MarketplaceAXSIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.Gametime}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.Gametime.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.Gametime)}
              icon={<MarketplaceGametimeIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces['Ticket Network']}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces['Ticket Network'].name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces['Ticket Network'])}
              icon={<MarketplaceTicketNetworkIcon size={18} />}
            />
            <SharingIconButton
              disabled={selectedListing.quantityReserved > 0}
              tooltipLabel={VLMarketplaces.GoTickets}
              selected={form.values.selectedMarketplaces.findIndex((m) => m.name === VLMarketplaces.GoTickets.name) !== -1}
              onClick={() => handleSelectionChange(VLMarketplaces.GoTickets)}
              icon={<MarketplaceGoTicketsIcon size={20} />}
            />
          </Flex>
          {showTooltip && (
            <Tooltip label="Selected listings have varying marketplace sharing values that may be overwritten." multiline maw={260}>
              <Center h={20} w={20} pos="absolute" top="calc(50% - 10px)" left={-32}>
                <WarningIcon size={20} color="var(--colors-yellow-warning)" />{' '}
              </Center>
            </Tooltip>
          )}
        </Box>
        <Group gap="xs" align="flex-end" justify="right" wrap="nowrap" w={236}>
          <BNButton
            size="xs"
            fullWidth
            onClick={() => {
              form.reset(); // revert to initialValues
              onClose?.();
            }}
            disabled={patchListingLoading || !form.isDirty()}
          >
            Cancel
          </BNButton>
          <BNButton
            disabled={selectedListing.quantityReserved > 0 || !form.isDirty()}
            fullWidth
            size="xs"
            variant="filled"
            color="green"
            loading={patchListingLoading}
            onClick={async () => {
              const listingResult = await updateInternalNotes(generateNewInternalNotes(selectedListing?.internalNotes ?? '', form.values.selectedMarketplaces));
              if (listingResult) {
                // sync form with updated internalNotes then reset dirty tracking
                const updated = getSelectedMarketplaces(listingResult.internalNotes || '');
                form.setValues({ selectedMarketplaces: updated });
                form.resetDirty();
              }
              if (bulk) {
                onClose?.();
              }
            }}
          >
            Apply
          </BNButton>
        </Group>
      </Flex>
    </BNAccordionMenu.Panel>
  );
}
